import type { MotionElement, MotionParticle } from "./types";
import { seededMotionRandom } from "./interpolation";
export function evaluateMotionParticles(
  element: MotionElement,
  time: number,
): MotionParticle[] {
  if (
    element.type !== "particles" ||
    time < element.start ||
    time >= element.end
  )
    return [];
  const local = time - element.start; // Particle speed is velocity, not timeline playback speed.
  const lifetime = element.lifetime ?? 2;
  const rate = element.rate ?? 20;
  const burst = element.count !== undefined;
  const seed = element.seed ?? 1;
  const last = burst ? element.count! : Math.floor(local * rate) + 1;
  const first = burst
    ? 0
    : Math.max(0, Math.floor((local - lifetime) * rate) + 1);
  const particles: MotionParticle[] = [];
  if (!burst && rate <= 0) return particles;
  for (let index = first; index < last && particles.length < 1000; index++) {
    const birth = burst ? 0 : index / rate;
    let age = local - birth;
    if (element.loop && burst) age %= lifetime;
    if (age < 0 || age >= lifetime) continue;
    const random = (offset: number) =>
      seededMotionRandom(seed, index * 11 + offset);
    const angle =
      (((element.direction ?? -90) +
        (random(0) - 0.5) * (element.spread ?? 360)) *
        Math.PI) /
      180;
    const velocity = (element.speed ?? 100) * (0.5 + random(1));
    const colors = element.colors ?? ["#ffffff"];
    const sizes = element.sizes ?? [12];
    particles.push({
      id: `${element.id}-particle-${index}`,
      x: (element.x ?? 0) + Math.cos(angle) * velocity * age,
      y:
        (element.y ?? 0) +
        Math.sin(angle) * velocity * age +
        ((element.gravity ?? 100) * age * age) / 2,
      rotation: random(2) * 360 + (element.spin ?? 0) * age,
      color: colors[Math.floor(random(3) * colors.length)],
      size: sizes[Math.floor(random(4) * sizes.length)],
      opacity:
        (element.opacity ?? 1) *
        (element.fadeOut === false ? 1 : 1 - age / lifetime),
      shape: element.shape ?? "circle",
      ...(element.emoji ? { emoji: element.emoji } : {}),
      ...(element.src ? { src: element.src } : {}),
      ...(element.name ? { name: element.name } : {}),
    });
  }
  return particles;
}
export function expandMotionRepeat(element: MotionElement): MotionElement[] {
  const shiftChildren = (
    children: MotionElement[] | undefined,
    delay: number,
  ): MotionElement[] | undefined =>
    children?.map((child) => ({
      ...child,
      start: child.start + delay,
      end: child.end + delay,
      ...(child.children
        ? { children: shiftChildren(child.children, delay) }
        : {}),
    }));
  const repeat = element.repeat;
  const count = repeat?.count ?? 1;
  return Array.from({ length: Math.min(1000, count) }, (_, index) => {
    if (!repeat) return { ...element };
    const keyframes = element.keyframes
      ? Object.fromEntries(
          Object.entries(element.keyframes).map(([property, frames]) => {
            const delta = ["x", "y", "rotation", "scale", "opacity"].includes(
              property,
            )
              ? Number(repeat[property as keyof typeof repeat] ?? 0) * index
              : 0;
            return [
              property,
              frames.map((frame) => ({
                ...frame,
                v: typeof frame.v === "number" ? frame.v + delta : frame.v,
              })),
            ];
          }),
        )
      : undefined;
    return {
      ...element,
      ...(keyframes ? { keyframes } : {}),
      id: `${element.id}-copy-${index}`,
      repeat: undefined,
      start: element.start + index * (repeat.delay ?? 0),
      end: element.end + index * (repeat.delay ?? 0),
      ...(element.children
        ? {
            children: shiftChildren(
              element.children,
              index * (repeat.delay ?? 0),
            ),
          }
        : {}),
      x: (element.x ?? 0) + index * (repeat.x ?? 0),
      y: (element.y ?? 0) + index * (repeat.y ?? 0),
      rotation: (element.rotation ?? 0) + index * (repeat.rotation ?? 0),
      scale: Math.max(0, (element.scale ?? 1) + index * (repeat.scale ?? 0)),
      opacity: Math.max(
        0,
        Math.min(1, (element.opacity ?? 1) + index * (repeat.opacity ?? 0)),
      ),
    };
  });
}
